import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useScreenSize from '../../core/hooks/useScreenSize';
import CategoryLink from '../CategoryLink/CategoryLink';
import Wrapper from '../common/Wrapper/Wrapper';
import ArticleHeader from '../Heading/Heading';
import {
  StyledBanner,
  StyledButton,
  StyledTopContent,
  StyledBottomContent,
  StyledDescription,
  StyledLogo,
  StyledTitle,
  StyledImage,
  StyledLabel,
  StyledImageContainer,
} from './SupportBanner.styles';

function SupportBanner({ data }) {
  if (!Object.entries(data).length) return null;

  const [screenSize] = useScreenSize();
  const [size, setSize] = useState(0);

  const bannerRef = useRef(null);
  const topContentRef = useRef(null);
  const bottomContentRef = useRef(null);
  const sizeRef = useRef(null);
  const dragRef = useRef(false);

  const handleMouseMove = useCallback(e => {
    const position = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    const wrapperHeight = topContentRef.current.scrollHeight;
    const height =
      position - 54 > sizeRef.current
        ? sizeRef.current
        : position - 54 < wrapperHeight + 50
        ? wrapperHeight + 50
        : position - 54;
    const percent = (height - wrapperHeight) / bottomContentRef.current.scrollHeight;
    bottomContentRef.current.style.opacity = percent;
    bannerRef.current.style.height = `${height}px`;
    document.querySelector('.app').style.paddingTop = `${height + 64}px`;
  }, []);

  const handleMouseDrag = () => {
    dragRef.current = true;
    document.body.addEventListener('mousemove', handleMouseMove);
  };

  const handleTouchDrag = () => {
    dragRef.current = true;
    document.body.addEventListener('touchmove', handleMouseMove);
  };

  const handleDragStop = useCallback(() => {
    if (!dragRef.current) return;

    document.body.removeEventListener('mousemove', handleMouseMove);
    document.body.removeEventListener('touchmove', handleMouseMove);
    bannerRef.current.style.transition = '0.2s';
    document.querySelector('.app').style.transition = 'padding 0.2s';

    if (
      bannerRef.current.offsetHeight >
      topContentRef.current.scrollHeight + bottomContentRef.current.scrollHeight / 1.5
    ) {
      bannerRef.current.style.height = `${sizeRef.current}px`;
      bottomContentRef.current.style.opacity = 1;
      document.querySelector('.app').style.paddingTop = `${sizeRef.current + 64}px`;
    } else {
      bannerRef.current.style.height = `${topContentRef.current.scrollHeight + 50}px`;
      bottomContentRef.current.style.opacity = 0;
      document.querySelector('.app').style.paddingTop = `${
        topContentRef.current.scrollHeight + 114
      }px`;
    }

    setTimeout(() => {
      bannerRef.current.style.transition = '0s';
      document.querySelector('.app').style.transition = '0s';
    }, 200);
    dragRef.current = false;
  }, []);

  const handleKeypress = e => {
    if (e.keyCode === 40 || e.keyCode === 38) {
      e.preventDefault();
      bannerRef.current.style.transition = '0.2s';
      document.querySelector('.app').style.transition = 'padding 0.2s';
      if (e.keyCode === 40) {
        bannerRef.current.style.height = `${sizeRef.current}px`;
        bottomContentRef.current.style.opacity = 1;
        document.querySelector('.app').style.paddingTop = `${sizeRef.current + 64}px`;
      }
      if (e.keyCode === 38) {
        bannerRef.current.style.height = `${topContentRef.current.scrollHeight + 50}px`;
        bottomContentRef.current.style.opacity = 0;
        document.querySelector('.app').style.paddingTop = `${
          topContentRef.current.scrollHeight + 114
        }px`;
      }
      setTimeout(() => {
        bannerRef.current.style.transition = '0s';
        document.querySelector('.app').style.transition = '0s';
      }, 200);
    }
  };

  useEffect(() => {
    setSize(topContentRef.current.scrollHeight + bottomContentRef.current.scrollHeight + 54);
    sizeRef.current =
      topContentRef.current.scrollHeight + bottomContentRef.current.scrollHeight + 54;
  }, [screenSize, data]);

  useEffect(() => {
    document.querySelector('.app').style.paddingTop = `${sizeRef.current + 64}px`;
    dragRef.current = true;
    handleDragStop();
  }, [size]);

  useEffect(() => {
    document.body.addEventListener('mouseup', handleDragStop);
    document.body.addEventListener('touchend', handleDragStop);

    bannerRef.current.addEventListener('touchmove', e => {
      e.preventDefault();
    });

    return () => {
      document.querySelector('.app').style.paddingTop = ``;
      document.body.removeEventListener('mouseup', handleDragStop);
      document.body.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('touchend', handleDragStop);
      document.body.removeEventListener('touchmove', handleMouseMove);
    };
  }, []);

  return (
    <StyledBanner ref={bannerRef}>
      <Wrapper>
        <StyledTopContent ref={topContentRef}>
          {(data.top.title || data.top.img) && (
            <StyledTitle>
              {data.top.img && (
                <StyledLogo src={data.top.img.src} alt={data.top.img.alt ? data.top.img.alt : ''} />
              )}
              {data.top.title && <ArticleHeader type="h2" styling="h3" title={data.top.title} />}
            </StyledTitle>
          )}
          <StyledDescription>{data.top.description}</StyledDescription>
        </StyledTopContent>
        <StyledBottomContent ref={bottomContentRef}>
          {data.bottom.category && (
            <CategoryLink
              link={data.bottom.category.link ? `/category/${data.bottom.category.link}` : '/'}
              color={data.bottom.category.type && data.bottom.category.type}
              name={data.bottom.category.name && data.bottom.category.name}
            />
          )}
          {data.bottom.img && (
            <StyledImageContainer>
              <StyledImage
                src={data.bottom.img.src}
                alt={data.bottom.img.alt ? data.bottom.img.alt : ''}
              />
            </StyledImageContainer>
          )}
          {data.bottom.label && <StyledLabel>{data.bottom.label}</StyledLabel>}
          {data.bottom.title && <ArticleHeader type="h3" styling="h2" title={data.bottom.title} />}
        </StyledBottomContent>
      </Wrapper>
      <StyledButton
        onMouseDown={handleMouseDrag}
        onTouchStart={handleTouchDrag}
        onKeyDown={handleKeypress}
      >
        <span className="sr-only">Drag banner</span>
      </StyledButton>
    </StyledBanner>
  );
}

SupportBanner.propTypes = {
  data: PropTypes.shape({
    top: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      img: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
      }),
    }),
    bottom: PropTypes.shape({
      category: PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        link: PropTypes.string,
      }),
      img: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
      }),
      label: PropTypes.string,
      title: PropTypes.string,
    }),
  }),
};

SupportBanner.defaultProps = {
  data: {},
};

export default SupportBanner;
